import React from "react";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const inputCls =
  "border-white/[0.1] bg-[#0A0A0A] text-sm focus-visible:border-white/30 focus-visible:ring-1 focus-visible:ring-white/20";

export function Field({ label, hint, children, className }) {
  return (
    <div className={cn("space-y-1.5", className)}>
      {label && <Label className="block text-xs font-medium text-muted-foreground">{label}</Label>}
      {children}
      {hint && <p className="text-xs text-muted-foreground/70">{hint}</p>}
    </div>
  );
}

export function TextField({ label, hint, value, onChange, testId, type = "text", placeholder, className, autoComplete, name, disabled, error }) {
  return (
    <Field label={label} hint={hint} className={className}>
      <Input
        data-testid={testId}
        type={type}
        name={name}
        autoComplete={autoComplete}
        value={value ?? ""}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={cn(inputCls, error && "border-[#EF4444] focus-visible:border-[#EF4444] focus-visible:ring-[#EF4444]/30")}
      />
      {error && <p className="text-xs text-[#EF4444]" data-testid={testId ? `${testId}-error` : undefined}>{error}</p>}
    </Field>
  );
}

export function TextareaField({ label, hint, value, onChange, testId, rows = 3 }) {
  return (
    <Field label={label} hint={hint}>
      <Textarea
        data-testid={testId}
        rows={rows}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className={inputCls}
      />
    </Field>
  );
}

export function SelectField({ label, hint, value, onChange, options, placeholder = "Select...", testId, className, triggerClass }) {
  return (
    <Field label={label} hint={hint} className={className}>
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger data-testid={testId} className={cn(inputCls, triggerClass)}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="max-h-72 border-white/[0.1] bg-[#1a1a1e]">
          {options.map((o) => {
            const val = typeof o === "string" ? o : String(o.value);
            const lab = typeof o === "string" ? o : o.label;
            return (
              <SelectItem key={val} value={val}>
                {lab}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </Field>
  );
}

export function SwitchField({ label, hint, checked, onChange, testId }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-white/[0.08] bg-[#0A0A0A] px-3 py-2.5">
      <div>
        <p className="text-sm text-foreground">{label}</p>
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </div>
      <Switch data-testid={testId} checked={!!checked} onCheckedChange={onChange} />
    </div>
  );
}


export function MultiChipField({ label, hint, value = [], onChange, options = [], testId }) {
  const selected = Array.isArray(value) ? value : [];
  const toggle = (v) =>
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  return (
    <Field label={label} hint={hint}>
      <div className="flex flex-wrap gap-1.5 rounded-lg border border-white/[0.1] bg-[#0A0A0A] p-2" data-testid={testId}>
        {options.map((o) => {
          const v = typeof o === "string" ? o : String(o.value);
          const lab = typeof o === "string" ? o : o.label;
          const on = selected.includes(v);
          return (
            <button
              type="button"
              key={v}
              onClick={() => toggle(v)}
              data-testid={testId ? `${testId}-${v}` : undefined}
              className={cn(
                "rounded-md px-2 py-1 text-xs font-medium transition-colors",
                on
                  ? "bg-white text-black"
                  : "border border-white/[0.12] bg-white/[0.03] text-muted-foreground hover:bg-white/[0.07] hover:text-foreground",
              )}
            >
              {lab}
            </button>
          );
        })}
      </div>
    </Field>
  );
}
